// "Check your inbox", shared by sign-up verification and password reset
// (DESIGN-SPEC §6.19): one tile, one text size, the address on its own line and
// one way back. Each use brings its own lead: verification can say "We sent a
// link to", but the enumeration-safe reset flow may send nothing, so its lead
// is conditional. "Resend email" (30 s cooldown) appears only where the server
// really sends again: sign-up verification. A reset request is limited to one
// email per 15 minutes on purpose, so its screen offers no resend that would
// claim a send which never happens.
import { useEffect, useRef, useState } from 'react';
import { MailIcon } from 'lucide-react';
import { AuthError } from '@/server-threads';
import { formatCopy } from '@/i18n/ns';
import type { EntryCopy } from '@/i18n/ns/entry';
import { EntryButton, EntryHeading } from './entry-shell';

export const RESEND_COOLDOWN_SECONDS = 30;

export interface EmailResend {
  /** Sends the email again; rejects with an AuthError code on failure. */
  send: () => Promise<unknown>;
  errorText: (code: string) => string;
}

function ResendAction({ copy, resend }: { copy: EntryCopy; resend: EmailResend }) {
  // The first email has just been sent, so the cooldown starts right away.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'status' | 'alert'; text: string } | null>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const send = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await resend.send();
      if (!active.current) return;
      setNotice({ tone: 'status', text: copy.resent });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (reason) {
      if (!active.current) return;
      const code = reason instanceof AuthError ? reason.code : 'generic';
      setNotice({ tone: 'alert', text: resend.errorText(code) });
    } finally {
      if (active.current) setBusy(false);
    }
  };

  return (
    <div className="pc-entry-actions">
      <EntryButton
        variant="secondary"
        block
        busy={busy}
        busyLabel={copy.sending}
        disabled={cooldown > 0}
        onClick={() => void send()}
      >
        {cooldown > 0 ? formatCopy(copy.resendIn, { seconds: cooldown }) : copy.resend}
      </EntryButton>
      {notice ? (
        <p
          role={notice.tone}
          className={notice.tone === 'alert' ? 'pc-entry-alert' : 'pc-entry-status'}
        >
          {notice.text}
        </p>
      ) : null}
    </div>
  );
}

export function EmailSentState({
  copy,
  lead,
  email,
  hint,
  resend,
  back,
}: {
  copy: EntryCopy;
  /** The words before the address; only a real send may claim "We sent". */
  lead: string;
  email: string;
  /** The words after the address. */
  hint: string;
  /** Present only when the server sends a new email on request. */
  resend?: EmailResend;
  back: { label: string; onClick: () => void };
}) {
  return (
    <>
      <EntryHeading
        tile={<MailIcon aria-hidden="true" />}
        title={copy.checkInboxTitle}
        lead={
          <>
            <span className="pc-entry-lead-line">{lead}</span>
            <span className="pc-entry-address">{email}</span>
            <span className="pc-entry-lead-line">{hint}</span>
          </>
        }
      />
      {resend ? <ResendAction copy={copy} resend={resend} /> : null}
      <div className="pc-entry-foot" data-alone={resend ? undefined : true}>
        <button type="button" className="pc-link" onClick={back.onClick}>
          {back.label}
        </button>
      </div>
    </>
  );
}
